export const BEHAVIOR_ASSESSMENT_VERSION = 'untangled-patterns-v1.1';
export const BEHAVIOR_ASSESSMENT_TITLE = 'The Untangled Patterns Assessment';
export const BEHAVIOR_ASSESSMENT_ITEM_COUNT = 120;
export const BEHAVIOR_ASSESSMENT_ESTIMATED_MINUTES = 22;

export const BEHAVIOR_RESPONSE_OPTIONS = [
  { value: 1, label: 'Almost never true' },
  { value: 2, label: 'Rarely true' },
  { value: 3, label: 'Sometimes true' },
  { value: 4, label: 'Often true' },
  { value: 5, label: 'Almost always true' },
] as const;

export type BehaviorDimensionKey =
  | 'selfTrust'
  | 'boundaryClarity'
  | 'emotionalRecovery'
  | 'reciprocity'
  | 'clearCommunication'
  | 'selfContinuity'
  | 'uncertaintyBalance'
  | 'digitalGrounding'
  | 'patternResponse'
  | 'supportAccess'
  | 'repairAccountability'
  | 'selfCompassion';

export type BehaviorQuestion = {
  id: string;
  dimension: BehaviorDimensionKey;
  text: string;
  reverse: boolean;
};

export type BehaviorScoreBand = 'needs-attention' | 'developing' | 'steady' | 'strong';

export type BehaviorDimensionScore = {
  key: BehaviorDimensionKey;
  label: string;
  shortLabel: string;
  score: number;
  band: BehaviorScoreBand;
  bandLabel: string;
  summary: string;
  suggestions: string[];
  book: { title: string; href: string };
};

export type BehaviorAssessmentResult = {
  assessmentVersion: string;
  itemCount: number;
  overallScore: number;
  overallLabel: string;
  dimensions: BehaviorDimensionScore[];
  strengthKeys: BehaviorDimensionKey[];
  focusKeys: BehaviorDimensionKey[];
  responseQuality: {
    level: 'review' | 'usable' | 'strong';
    label: string;
    note: string;
  };
};

type DimensionDefinition = {
  label: string;
  shortLabel: string;
  lowSummary: string;
  middleSummary: string;
  highSummary: string;
  suggestions: [string, string, string];
  book: { title: string; href: string };
};

export const BEHAVIOR_DIMENSION_ORDER: BehaviorDimensionKey[] = [
  'selfTrust',
  'boundaryClarity',
  'emotionalRecovery',
  'reciprocity',
  'clearCommunication',
  'selfContinuity',
  'uncertaintyBalance',
  'digitalGrounding',
  'patternResponse',
  'supportAccess',
  'repairAccountability',
  'selfCompassion',
];

export const BEHAVIOR_DIMENSIONS: Record<BehaviorDimensionKey, DimensionDefinition> = {
  selfTrust: {
    label: 'Inner compass',
    shortLabel: 'Self-trust',
    lowSummary: 'You may hand your interpretation to other people quickly, especially after confusing or emotionally charged moments.',
    middleSummary: 'You can hear your own judgment, but uncertainty or another person’s confidence can sometimes override it.',
    highSummary: 'You generally stay connected to your observations, values, and emotional signals while remaining open to new facts.',
    suggestions: [
      'After a difficult interaction, write down observable facts before asking anyone else what they think.',
      'Use the sentence “My first reading of this is…” and let it exist before revising it.',
      'Choose one low-stakes decision each day without seeking reassurance afterward.',
    ],
    book: { title: 'The Unsettling Feeling', href: '/titles/the-unsettling-feeling' },
  },
  boundaryClarity: {
    label: 'Boundary clarity',
    shortLabel: 'Boundaries',
    lowSummary: 'You may notice your limits late, explain them at length, or soften them until they no longer protect what matters.',
    middleSummary: 'You can state many limits, though guilt, pressure, or fear of disconnection may make consistency harder.',
    highSummary: 'You tend to recognise, communicate, and maintain limits without treating another person’s disappointment as proof you are wrong.',
    suggestions: [
      'Name the limit in one sentence, without building a courtroom case for it.',
      'Decide the action you will take if a boundary is ignored; a boundary needs a response, not only a request.',
      'Practise a 24-hour pause before agreeing to requests that create pressure or resentment.',
    ],
    book: { title: "Please Don’t Say You Love Me", href: '/titles/please-dont-say-you-love-me' },
  },
  emotionalRecovery: {
    label: 'Emotional recovery',
    shortLabel: 'Recovery',
    lowSummary: 'Relational stress may stay active for a long time and pull you toward urgent contact, rumination, or decisions made for immediate relief.',
    middleSummary: 'You have some ways to settle yourself, but intense uncertainty can still narrow your choices.',
    highSummary: 'You can usually make room for strong emotion, lower the urgency, and choose your next step after your system has settled.',
    suggestions: [
      'Create a 20-minute no-action window when an interaction triggers urgency.',
      'Use one body-based reset—walking, paced breathing, cold water, or stretching—before analysing the relationship.',
      'Separate “I need relief now” from “I need to decide what this relationship means.”',
    ],
    book: { title: 'Untangling the Spark', href: '/titles/untangling-the-spark' },
  },
  reciprocity: {
    label: 'Reciprocity',
    shortLabel: 'Mutuality',
    lowSummary: 'You may compensate for uneven effort, explain away inconsistency, or measure connection by your investment rather than mutual behaviour.',
    middleSummary: 'You value mutual effort but may keep carrying more than your share when hope or chemistry is strong.',
    highSummary: 'You generally look for consistent mutual effort and allow another person’s behaviour—not your potential for them—to inform your choices.',
    suggestions: [
      'Compare promises with repeated behaviour over time, not with the most intense moment.',
      'Stop one compensating behaviour for two weeks and observe whether the connection still moves toward you.',
      'Write down what mutual effort looks like in contact, planning, care, and repair.',
    ],
    book: { title: "Please Don’t Say You Love Me", href: '/titles/please-dont-say-you-love-me' },
  },
  clearCommunication: {
    label: 'Clear communication',
    shortLabel: 'Communication',
    lowSummary: 'You may rely on hints, over-explaining, silence, or mind-reading when direct communication feels risky.',
    middleSummary: 'You can communicate directly in calmer moments, while conflict or fear of rejection may make your message less clear.',
    highSummary: 'You tend to say what you mean, ask for clarification, and distinguish a request from an expectation or test.',
    suggestions: [
      'Use: “When X happened, I felt Y, and I am asking for Z.”',
      'Replace one assumption with a direct, answerable question.',
      'Keep important messages short enough that the central request cannot disappear inside the explanation.',
    ],
    book: { title: "Please Don’t Say You Love Me", href: '/titles/please-dont-say-you-love-me' },
  },
  selfContinuity: {
    label: 'Self-continuity',
    shortLabel: 'Identity',
    lowSummary: 'Closeness may pull you away from routines, preferences, friendships, or plans that help you feel like yourself.',
    middleSummary: 'You retain important parts of your life, though a compelling relationship can still become the organising centre.',
    highSummary: 'You usually stay connected to your values, relationships, interests, and future while making room for closeness.',
    suggestions: [
      'Protect two recurring commitments that belong to your life independently of a relationship.',
      'Notice preferences you have started editing to seem easier, more desirable, or less demanding.',
      'Make one weekly choice based only on the life you want to build.',
    ],
    book: { title: 'Finding Your Path Home', href: '/titles/finding-your-path-home' },
  },
  uncertaintyBalance: {
    label: 'Uncertainty balance',
    shortLabel: 'Uncertainty',
    lowSummary: 'Ambiguity may trigger reassurance-seeking, rapid conclusions, or repeated checking that briefly lowers anxiety but keeps the cycle active.',
    middleSummary: 'You can tolerate some uncertainty, although silence, delayed replies, or unclear status may still take over your attention.',
    highSummary: 'You can usually remain grounded while information is incomplete and wait for patterns to emerge before acting.',
    suggestions: [
      'Choose a fixed time to revisit an unanswered question instead of checking continuously.',
      'List three plausible explanations before treating the most painful one as fact.',
      'Ask once for needed clarity, then evaluate the quality of the answer and subsequent behaviour.',
    ],
    book: { title: "Please Don’t Say You Love Me", href: '/titles/please-dont-say-you-love-me' },
  },
  digitalGrounding: {
    label: 'Digital grounding',
    shortLabel: 'Digital habits',
    lowSummary: 'Feeds, read receipts, profiles, or online comparison may shape your emotional state and relationship story more than you want.',
    middleSummary: 'You notice digital influence and sometimes interrupt it, though certain triggers can still pull you into checking or comparison.',
    highSummary: 'You generally use digital information as one input rather than letting it define your worth, safety, or interpretation of a relationship.',
    suggestions: [
      'Remove one high-trigger shortcut, notification, or saved search for seven days.',
      'Before checking, name what you hope the screen will make you feel.',
      'Move important relationship questions out of indirect online evidence and into direct conversation or observation.',
    ],
    book: { title: 'The Perfect Delusion', href: '/titles/the-perfect-delusion' },
  },
  patternResponse: {
    label: 'Pattern recognition and response',
    shortLabel: 'Pattern response',
    lowSummary: 'You may recognise concerning behaviour but postpone your response while waiting for certainty, a better explanation, or a return to the beginning.',
    middleSummary: 'You can name many patterns, though hope, intensity, or isolated good moments may slow your response.',
    highSummary: 'You tend to evaluate repeated behaviour, take your unease seriously, and adjust access when words and actions remain misaligned.',
    suggestions: [
      'Track repeated events by date, behaviour, impact, and repair rather than by labels.',
      'Decide in advance which patterns require distance, support, or a professional safety conversation.',
      'Ask whether the relationship is improving in practice—not whether the other person can explain why it has not.',
    ],
    book: { title: 'The Unsettling Feeling', href: '/titles/the-unsettling-feeling' },
  },
  supportAccess: {
    label: 'Support access',
    shortLabel: 'Support',
    lowSummary: 'You may isolate, minimise what is happening, or wait until a situation becomes overwhelming before reaching for support.',
    middleSummary: 'You have some trusted support, but shame, privacy, or fear of being judged can keep important details hidden.',
    highSummary: 'You generally know who can offer perspective, practical help, or qualified support and can reach out before urgency peaks.',
    suggestions: [
      'Identify three kinds of support: emotional, practical, and qualified professional help.',
      'Tell one trusted person the unedited version of a situation instead of only the part that feels easy to defend.',
      'If there is coercion, stalking, threats, control, or danger, prioritise local professional safety support over relationship analysis.',
    ],
    book: { title: 'Untangling the Spark', href: '/titles/untangling-the-spark' },
  },
  repairAccountability: {
    label: 'Repair and accountability',
    shortLabel: 'Repair',
    lowSummary: 'Conflict may end through avoidance, reassurance, blame, or a return to normal without a clear repair or changed behaviour.',
    middleSummary: 'You value accountability but may accept apologies that do not yet include ownership, repair, and follow-through.',
    highSummary: 'You generally look for specific ownership, mutual repair, and observable change rather than treating relief as resolution.',
    suggestions: [
      'Define repair as four parts: naming the impact, owning the choice, making amends, and changing the pattern.',
      'After an apology, ask what will be different next time and observe whether it happens.',
      'Notice whether conflict makes the issue clearer or leaves you defending your right to raise it.',
    ],
    book: { title: 'The Unsettling Feeling', href: '/titles/the-unsettling-feeling' },
  },
  selfCompassion: {
    label: 'Self-compassion',
    shortLabel: 'Self-kindness',
    lowSummary: 'You may use shame, harsh self-judgment, or perfectionism to make sense of relational mistakes and difficult feelings.',
    middleSummary: 'You can offer yourself kindness at times, though setbacks may still become evidence that you should have known better.',
    highSummary: 'You generally hold yourself accountable without humiliation and treat learning as a process rather than a verdict on your worth.',
    suggestions: [
      'Describe one mistake using the same tone you would use with someone you respect.',
      'Replace “I should have known” with “What information and capacity did I have then?”',
      'Choose one corrective action without turning it into a punishment.',
    ],
    book: { title: 'Finding Your Path Home', href: '/titles/finding-your-path-home' },
  },
};

type Statement = { text: string; reverse?: boolean };

const STATEMENTS: Record<BehaviorDimensionKey, Statement[]> = {
  selfTrust: [
    { text: 'I can name what I feel before asking someone else what it means.' },
    { text: 'I trust my memory of a difficult conversation even when another person disagrees.' },
    { text: 'When something feels off, I pause and investigate instead of dismissing it.' },
    { text: 'I can make relationship decisions without collecting many outside opinions.' },
    { text: 'My choices usually reflect my values even when someone is disappointed.' },
    { text: 'I assume I am overreacting before considering that my concern may be valid.', reverse: true },
    { text: 'I change my interpretation quickly when someone insists I misunderstood.', reverse: true },
    { text: 'I need repeated reassurance that my feelings make sense.', reverse: true },
    { text: 'I ignore early discomfort until there is undeniable proof.', reverse: true },
    { text: 'After conflict, I doubt my perception more than I review the facts.', reverse: true },
  ],
  boundaryClarity: [
    { text: 'I notice when a request crosses a personal limit.' },
    { text: 'I can say no without giving a long explanation.' },
    { text: 'I decide what I will do if an important boundary is ignored.' },
    { text: 'I can let someone be disappointed without immediately changing my answer.' },
    { text: 'My boundaries stay similar even when I strongly want someone to like me.' },
    { text: 'I agree to things and only realise later that I feel resentful.', reverse: true },
    { text: 'I soften a limit until it no longer changes anything.', reverse: true },
    { text: 'I treat another person’s anger as evidence that my boundary was unfair.', reverse: true },
    { text: 'I keep negotiating after I have already reached my limit.', reverse: true },
    { text: 'I say yes because refusing feels more uncomfortable than the request itself.', reverse: true },
  ],
  emotionalRecovery: [
    { text: 'I can delay sending a message until emotional urgency has passed.' },
    { text: 'I have reliable ways to settle my body after a stressful interaction.' },
    { text: 'I can feel a strong emotion without making an immediate relationship decision.' },
    { text: 'After conflict, I return to daily responsibilities within a reasonable time.' },
    { text: 'I can distinguish wanting relief from needing to take action.' },
    { text: 'A delayed reply can disrupt most of my day.', reverse: true },
    { text: 'I contact someone repeatedly because the uncertainty feels unbearable.', reverse: true },
    { text: 'I replay conversations until I feel physically and mentally exhausted.', reverse: true },
    { text: 'I make major decisions mainly to stop an intense feeling.', reverse: true },
    { text: 'Once activated, I struggle to notice information that could calm the situation.', reverse: true },
  ],
  reciprocity: [
    { text: 'I notice whether effort is mutual over time.' },
    { text: 'I allow another person’s consistency to influence how much I invest.' },
    { text: 'I expect care, planning, and curiosity to move in both directions.' },
    { text: 'I can step back when I am carrying the connection alone.' },
    { text: 'I evaluate a relationship by repeated behaviour rather than potential.' },
    { text: 'I create explanations for why someone cannot meet basic agreements.', reverse: true },
    { text: 'I increase my effort when the other person becomes less consistent.', reverse: true },
    { text: 'A few intense moments outweigh long periods of uneven effort for me.', reverse: true },
    { text: 'I feel responsible for keeping the connection moving.', reverse: true },
    { text: 'I accept less care because I know the other person has difficulties.', reverse: true },
  ],
  clearCommunication: [
    { text: 'I can make a direct request without hiding it inside a long explanation.' },
    { text: 'I ask for clarification instead of relying on hints or online clues.' },
    { text: 'I can describe impact without claiming to know another person’s motives.' },
    { text: 'I say when I need time before continuing a difficult conversation.' },
    { text: 'I can hear a disappointing answer without pretending I never asked.' },
    { text: 'I expect people to understand what I need without my saying it clearly.', reverse: true },
    { text: 'I use silence or distance to make someone notice I am hurt.', reverse: true },
    { text: 'My main point gets lost because I explain every detail.', reverse: true },
    { text: 'I avoid asking clear questions because I may not like the answer.', reverse: true },
    { text: 'I agree outwardly and hope the other person senses that I disagree.', reverse: true },
  ],
  selfContinuity: [
    { text: 'I keep important routines when I begin a close relationship.' },
    { text: 'I stay connected to friends and interests that matter to me.' },
    { text: 'I can name what I want apart from what a partner or potential partner wants.' },
    { text: 'My future plans remain visible to me when I am emotionally invested.' },
    { text: 'I let people know my real preferences even when they differ from theirs.' },
    { text: 'I rearrange most of my life around a new connection.', reverse: true },
    { text: 'I become less certain about my goals when someone important wants something different.', reverse: true },
    { text: 'I edit my personality to seem easier to love.', reverse: true },
    { text: 'My mood and schedule become organised around another person’s availability.', reverse: true },
    { text: 'I lose contact with parts of myself when a relationship becomes intense.', reverse: true },
  ],
  uncertaintyBalance: [
    { text: 'I can wait for more information before deciding what a silence or delay means.' },
    { text: 'I can tolerate not knowing exactly where I stand for a limited period.' },
    { text: 'I ask once for clarity and then observe the quality of the response.' },
    { text: 'I can keep my attention on my own day while a relationship question is unresolved.' },
    { text: 'I consider several explanations before believing the most painful one.' },
    { text: 'I repeatedly check messages, status indicators, or profiles for reassurance.', reverse: true },
    { text: 'I treat uncertainty as a sign that something bad is happening.', reverse: true },
    { text: 'I push for immediate certainty even when neither person has enough information.', reverse: true },
    { text: 'A vague message can lead me to build a complete negative story.', reverse: true },
    { text: 'I ask the same question in different ways because the first answer did not settle me.', reverse: true },
  ],
  digitalGrounding: [
    { text: 'I can put my phone away when checking begins to change my mood.' },
    { text: 'I remember that an online profile is a selected version of a life.' },
    { text: 'I bring important questions into direct conversation instead of decoding posts.' },
    { text: 'I can see an upsetting online detail without acting on it immediately.' },
    { text: 'My sense of worth stays relatively stable when I compare myself online.' },
    { text: 'I check who someone follows, likes, or interacts with to feel safer.', reverse: true },
    { text: 'Read receipts or activity indicators strongly affect my interpretation of a relationship.', reverse: true },
    { text: 'I compare my appearance or relationship to curated online examples.', reverse: true },
    { text: 'I use online evidence to answer questions I am afraid to ask directly.', reverse: true },
    { text: 'I keep checking even after I know it is making me feel worse.', reverse: true },
  ],
  patternResponse: [
    { text: 'I pay attention when words and repeated actions do not match.' },
    { text: 'I record facts when a situation repeatedly leaves me confused.' },
    { text: 'I adjust someone’s access to me when a harmful pattern continues.' },
    { text: 'I can take unease seriously without first assigning a dramatic label.' },
    { text: 'I look for changed behaviour after a promise or apology.' },
    { text: 'I wait for perfect proof before responding to a repeated concern.', reverse: true },
    { text: 'A return to warmth makes me forget a pattern that has not been repaired.', reverse: true },
    { text: 'I focus on why someone behaves badly more than on its impact on me.', reverse: true },
    { text: 'I remain in confusing situations because the beginning felt unusually intense.', reverse: true },
    { text: 'I treat each troubling event as separate even when the same theme repeats.', reverse: true },
  ],
  supportAccess: [
    { text: 'I know who I can contact for honest perspective.' },
    { text: 'I can share difficult relationship details without editing them to protect someone’s image.' },
    { text: 'I seek support before a situation becomes overwhelming.' },
    { text: 'I can distinguish friendly advice from help that requires a qualified professional.' },
    { text: 'I keep practical safety options visible when a situation involves control or threats.' },
    { text: 'I isolate when I feel ashamed or confused about a relationship.', reverse: true },
    { text: 'I minimise events so people close to me will not worry.', reverse: true },
    { text: 'I avoid qualified help because I believe I should solve everything alone.', reverse: true },
    { text: 'I stop telling people what is happening when they express concern.', reverse: true },
    { text: 'I wait until a crisis before asking for practical support.', reverse: true },
  ],
  repairAccountability: [
    { text: 'I look for specific ownership when someone apologises.' },
    { text: 'I can name my part in a conflict without taking responsibility for everything.' },
    { text: 'I expect repair to include different behaviour over time.' },
    { text: 'I can revisit an issue if the first conversation did not resolve it.' },
    { text: 'I notice whether conflict creates clarity or more confusion.' },
    { text: 'Relief after conflict feels like enough even when nothing changes.', reverse: true },
    { text: 'I accept “I am sorry you feel that way” as full accountability.', reverse: true },
    { text: 'I apologise quickly to end tension even when I do not understand what I did.', reverse: true },
    { text: 'I let affection replace a conversation about what happened.', reverse: true },
    { text: 'I treat promises to change as if the change has already occurred.', reverse: true },
  ],
  selfCompassion: [
    { text: 'I can review a mistake without attacking my character.' },
    { text: 'I allow myself to learn information later than I wish I had.' },
    { text: 'I can take corrective action without using shame as motivation.' },
    { text: 'I speak to myself with respect after rejection or disappointment.' },
    { text: 'I recognise that caring about someone does not make me foolish.' },
    { text: 'I tell myself I should have known better whenever I miss a warning sign.', reverse: true },
    { text: 'I treat relationship difficulties as evidence that something is wrong with me.', reverse: true },
    { text: 'I replay embarrassing moments as a way to punish myself.', reverse: true },
    { text: 'I believe harsh self-criticism is necessary to stop repeating mistakes.', reverse: true },
    { text: 'I find it easier to understand another person’s behaviour than to forgive my own.', reverse: true },
  ],
};

export const BEHAVIOR_QUESTIONS: BehaviorQuestion[] = Array.from({ length: 10 }, (_, itemIndex) =>
  BEHAVIOR_DIMENSION_ORDER.map((dimension) => {
    const statement = STATEMENTS[dimension][itemIndex];
    return {
      id: `${dimension}-${String(itemIndex + 1).padStart(2, '0')}`,
      dimension,
      text: statement.text,
      reverse: Boolean(statement.reverse),
    };
  }),
).flat();

if (BEHAVIOR_QUESTIONS.length !== BEHAVIOR_ASSESSMENT_ITEM_COUNT) {
  throw new Error(`Assessment item bank expected ${BEHAVIOR_ASSESSMENT_ITEM_COUNT} questions, found ${BEHAVIOR_QUESTIONS.length}.`);
}

export function behaviorScoreBand(score: number): { band: BehaviorScoreBand; label: string } {
  if (score < 40) return { band: 'needs-attention', label: 'Needs attention' };
  if (score < 65) return { band: 'developing', label: 'Developing' };
  if (score < 80) return { band: 'steady', label: 'Steady' };
  return { band: 'strong', label: 'Strong' };
}

function scoreSummary(definition: DimensionDefinition, score: number) {
  if (score < 40) return definition.lowSummary;
  if (score < 65) return definition.middleSummary;
  return definition.highSummary;
}

export function scoreBehaviorAssessment(
  answers: Record<string, number>,
  durationSeconds: number,
): BehaviorAssessmentResult {
  const values = BEHAVIOR_QUESTIONS.map((question) => {
    const raw = Number(answers[question.id]);
    if (!Number.isInteger(raw) || raw < 1 || raw > 5) {
      throw new Error(`A valid response is required for ${question.id}.`);
    }
    return { question, raw, scored: question.reverse ? 6 - raw : raw };
  });

  const dimensions = BEHAVIOR_DIMENSION_ORDER.map((key): BehaviorDimensionScore => {
    const items = values.filter((value) => value.question.dimension === key);
    const score = Math.round(((items.reduce((sum, item) => sum + item.scored, 0) / items.length - 1) / 4) * 100);
    const definition = BEHAVIOR_DIMENSIONS[key];
    const band = behaviorScoreBand(score);
    return {
      key,
      label: definition.label,
      shortLabel: definition.shortLabel,
      score,
      band: band.band,
      bandLabel: band.label,
      summary: scoreSummary(definition, score),
      suggestions: [...definition.suggestions],
      book: definition.book,
    };
  });

  const sorted = [...dimensions].sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));
  const rawValues = values.map((value) => value.raw);
  const uniqueResponses = new Set(rawValues).size;
  const mean = rawValues.reduce((sum, value) => sum + value, 0) / rawValues.length;
  const variance = rawValues.reduce((sum, value) => sum + (value - mean) ** 2, 0) / rawValues.length;
  const tooFast = durationSeconds > 0 && durationSeconds < 8 * 60;
  const lowVariation = uniqueResponses < 3 || variance < 0.22;
  const quality = tooFast || lowVariation
    ? {
        level: 'review' as const,
        label: 'Worth reviewing',
        note: 'Your answers were complete, but the pace or response pattern was unusually uniform. Treat the report as a prompt and retake it slowly if it does not feel accurate.',
      }
    : durationSeconds >= 15 * 60 && uniqueResponses >= 4
      ? {
          level: 'strong' as const,
          label: 'Strong response quality',
          note: 'Your completion pace and answer variation support a more nuanced reflection. This still is not a diagnosis or population percentile.',
        }
      : {
          level: 'usable' as const,
          label: 'Usable response quality',
          note: 'Your response pattern is suitable for reflection. Scores describe this set of answers and are not diagnostic or normative percentiles.',
        };

  const overallScore = Math.round(dimensions.reduce((sum, dimension) => sum + dimension.score, 0) / dimensions.length);
  return {
    assessmentVersion: BEHAVIOR_ASSESSMENT_VERSION,
    itemCount: BEHAVIOR_QUESTIONS.length,
    overallScore,
    overallLabel: 'Relational clarity index',
    dimensions,
    focusKeys: sorted.slice(0, 3).map((dimension) => dimension.key),
    strengthKeys: sorted.slice(-3).reverse().map((dimension) => dimension.key),
    responseQuality: quality,
  };
}

export function publicBehaviorQuestion(question: BehaviorQuestion) {
  return { id: question.id, text: question.text };
}
